import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
  // High refresh + force GPU layers for smoother scroll on long pages
  gsap.config({ force3D: true, autoSleep: 60 });
  ScrollTrigger.config({ ignoreMobileResize: true });
}

/**
 * Lightweight scroll-reveal wrapper (single GSAP tween per instance).
 *
 * Animates a single target — the root element itself — instead of staggering
 * every child. This keeps each section to one ScrollTrigger and one tween,
 * which is dramatically lighter than per-child staggers and avoids the
 * "feel pesado" symptom on long pages with many sections.
 *
 * If you need per-child stagger (e.g. pillars columns), pass `stagger={true}`
 * and the component will instead animate direct children with a small,
 * cheap stagger.
 */
const Reveal = ({
  children,
  stagger = false,
  y = 18,
  duration = 0.7,
  start = 'top 88%',
  as: Tag = 'div',
  className,
  style,
  testId,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const node = ref.current;
    if (!node) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const ctx = gsap.context(() => {
      const targets = stagger
        ? node.querySelectorAll(':scope > *')
        : [node];

      if (!targets.length) return;

      gsap.set(targets, { opacity: 0, y, willChange: 'opacity, transform' });
      gsap.to(targets, {
        opacity: 1,
        y: 0,
        duration,
        ease: 'power2.out',
        stagger: stagger ? 0.08 : 0,
        scrollTrigger: {
          trigger: node,
          start,
          once: true,
          fastScrollEnd: true,
        },
        onComplete: () => {
          // Free up the compositor once we're done
          targets.forEach((el) => {
            if (el && el.style) el.style.willChange = 'auto';
          });
        },
      });
    }, node);

    return () => ctx.revert();
  }, [stagger, y, duration, start]);

  return (
    <Tag ref={ref} className={className} style={style} data-testid={testId}>
      {children}
    </Tag>
  );
};

export default Reveal;
